import axios from 'axios';

import { getApiBaseUrl } from '../config/ApiConfig';
import { getMidwayJwtToken } from '../auth/MidwayJwtToken';

// --- Types and Interfaces ---

export type JobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface AttributionRequest {
  asin: string;
  site?: string;
  fgid?: number;
  horizon: number;
  span: number;
  fcd: string;
  region: string;
  business: string;
  n_steps?: number;
}

export interface DivergenceRequest {
  asin?: string;
  site?: string;
  fgid?: number;
  region: string;
  business: string;
  start_date: string;
  end_date: string;
}

export interface JobSubmission {
  job_id: string;
  status: JobStatus;
  submitted_at: string;
}

export interface FeatureAttribution {
  feature_name: string;
  category: 'static' | 'temporal';
  attribution_score: number;
  rank: number;
}

export interface AttributionResults {
  static_features: FeatureAttribution[];
  temporal_features: FeatureAttribution[];
  convergence_delta: number;
  forecast_output: number;
  baseline_output: number;
}

export interface Job {
  job_id: string;
  status: JobStatus;
  asin: string;
  site?: string;
  fgid?: number;
  horizon: number;
  span: number;
  fcd: string;
  region: string;
  business: string;
  target_quantile?: string;
  submitted_at: string;
  started_at?: string;
  completed_at?: string;
  results?: AttributionResults;
  error_message?: string;
  error_category?: string;
}

export interface RunSearchFilters {
  asin: string;
  site?: string;
  fgid?: number;
  region: string;
  business: string;
  start_date: string;
  end_date: string;
}

export interface ForecastRunSummary {
  execution_date: string;
  asin: string;
  site: string;
  fgid: number;
  horizon: number;
  span: number;
}

export interface DivergencePoint {
  fcd: string;
  p50: number;
  p90: number;
}

export interface DivergenceResponse {
  asin: string;
  site?: string;
  fgid?: number;
  region: string;
  business: string;
  data_points: DivergencePoint[];
  insufficient_data: boolean;
}

export interface DivergenceViewRow {
  view: string;
  quantile: string;
  first: number;
  last: number;
  change: number;
  change_pct: number;
  min: number;
  max: number;
  note: string;
}

export interface AgentExplanation {
  summary: string;
  view_breakdown?: DivergenceViewRow[];
  top_features: Array<{
    name: string;
    score: number;
    direction: string;
    explanation: string;
  }>;
  next_steps: string[];
  generated_at: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  role: 'assistant';
  content: string;
  session_id: string;
}

// --- Auth Helper ---

async function authHeader(): Promise<Record<string, string>> {
  const token = await getMidwayJwtToken();
  return { Authorization: `Bearer ${token}` };
}

// --- Polling Constants ---

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// --- API Functions ---

/**
 * Submit a new attribution request. Returns the job submission details
 * including a unique job_id for tracking.
 */
export async function submitAttribution(
  request: AttributionRequest,
  signal?: AbortSignal
): Promise<JobSubmission> {
  const response = await axios.post<JobSubmission>(
    `${getApiBaseUrl()}/attribution/jobs`,
    request,
    { headers: await authHeader(), signal }
  );
  return response.data;
}

/**
 * Fetch the current status and results (if completed) for a given job.
 */
export async function getJobStatus(
  jobId: string,
  signal?: AbortSignal
): Promise<Job> {
  const response = await axios.get<Job>(
    `${getApiBaseUrl()}/attribution/jobs/${encodeURIComponent(jobId)}`,
    { headers: await authHeader(), signal }
  );
  return response.data;
}

/**
 * Retrieve the most recent explainability jobs, sorted by submission time
 * descending. Defaults to 10 if no limit is specified.
 */
export async function getRecentJobs(
  limit?: number,
  signal?: AbortSignal
): Promise<Job[]> {
  const response = await axios.get<Job[]>(
    `${getApiBaseUrl()}/attribution/jobs/recent`,
    {
      params: limit != null ? { limit } : undefined,
      headers: await authHeader(),
      signal,
    }
  );
  return response.data;
}

/**
 * Search for forecast runs matching the given filters. Returns up to 100
 * matching run summaries for user selection.
 */
export async function searchRuns(
  filters: RunSearchFilters,
  signal?: AbortSignal
): Promise<ForecastRunSummary[]> {
  const response = await axios.post<ForecastRunSummary[]>(
    `${getApiBaseUrl()}/attribution/jobs/search`,
    filters,
    { headers: await authHeader(), signal }
  );
  return response.data;
}

/**
 * Retrieve forecast-over-forecast divergence data (P50/P90 per FCD)
 * for the specified parameters and date range.
 */
export async function getDivergence(
  request: DivergenceRequest,
  signal?: AbortSignal
): Promise<DivergenceResponse> {
  const params: Record<string, string | number> = {
    region: request.region,
    business: request.business,
    start_date: request.start_date,
    end_date: request.end_date,
  };
  if (request.asin != null) params.asin = request.asin;
  if (request.site != null) params.site = request.site;
  if (request.fgid != null) params.fgid = request.fgid;

  const response = await axios.get<DivergenceResponse>(
    `${getApiBaseUrl()}/divergence`,
    {
      params,
      headers: await authHeader(),
      signal,
    }
  );
  return response.data;
}

/**
 * Invoke the Explainability Agent to generate a natural language
 * explanation of a completed attribution job's results.
 */
export async function invokeAgent(
  jobId: string,
  signal?: AbortSignal
): Promise<AgentExplanation> {
  const response = await axios.post<AgentExplanation>(
    `${getApiBaseUrl()}/agent/explain`,
    { job_id: jobId },
    { headers: await authHeader(), signal }
  );
  return response.data;
}

/**
 * Invoke the Explainability Agent with inline IG results (no DDB lookup).
 * Used when results come directly from Batch/S3 rather than a DDB job.
 */
export async function invokeAgentWithResults(
  results: unknown[],
  metadata: Record<string, string>,
  signal?: AbortSignal
): Promise<AgentExplanation> {
  const response = await axios.post<AgentExplanation>(
    `${getApiBaseUrl()}/agent/explain-results`,
    { results, metadata },
    { headers: await authHeader(), signal }
  );
  return response.data;
}

/**
 * Send a message to the conversational explainability agent.
 * Includes full conversation history for multi-turn context.
 */
export async function chatWithAgent(
  messages: ChatMessage[],
  results: unknown[],
  metadata: Record<string, string>,
  sessionId?: string,
  signal?: AbortSignal
): Promise<ChatResponse> {
  const response = await axios.post<ChatResponse>(
    `${getApiBaseUrl()}/agent/chat`,
    { messages, results, metadata, session_id: sessionId },
    { headers: await authHeader(), signal }
  );
  return response.data;
}

// --- Polling Helper ---

/**
 * Polls a job's status until it reaches a terminal state (completed or failed).
 * Times out after 5 minutes. Pass an AbortSignal to cancel an in-flight poll loop.
 */
export async function pollJobUntilComplete(
  jobId: string,
  signal?: AbortSignal
): Promise<Job> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new DOMException('Job polling cancelled', 'AbortError');
    }

    const job = await getJobStatus(jobId, signal);

    if (job.status === 'completed' || job.status === 'failed') {
      return job;
    }

    await delay(POLL_INTERVAL_MS);
  }

  throw new Error('Job polling timed out while waiting for completion');
}
